/**
 * A new job from the office — the phone call that starts most of the work
 * (JOB-1). Customer, site, what's wrong. Booking it onto someone is optional:
 * left alone it lands in Unassigned on Dispatch, where it can be dragged.
 *
 * A callout is stamped as reported now, so a site on a response agreement
 * gets its SLA clock from the moment the customer rang, not from whenever the
 * job is booked.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ApiError, api } from "../lib/api";
import { SLOTS, hhmm, minuteOfSlot, todayIn } from "../lib/board";
import { titleCase } from "../lib/format";
import { BILLING_LABEL, type BillingMode, defaultBillingMode, parseKmOneWay } from "../lib/servicerates";
import { CustomerSitePicker } from "../components/CustomerSitePicker";
import { Card, PageHeader } from "../components/ui";

const TYPES = ["callout", "service", "install", "maintenance", "quote_visit", "warranty"] as const;
type JobType = (typeof TYPES)[number];

export default function NewJob() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [customerId, setCustomerId] = useState(params.get("customer") ?? "");
  const [siteId, setSiteId] = useState(params.get("site") ?? "");
  const [type, setType] = useState<JobType>("callout");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [po, setPo] = useState("");
  const [hours, setHours] = useState("");
  const [book, setBook] = useState(false);
  const [tech, setTech] = useState("");
  const [day, setDay] = useState(todayIn("Australia/Adelaide"));
  const [at, setAt] = useState("first");
  const [problem, setProblem] = useState<string | null>(null);
  // "How this bills": what the office picked, or null to follow the default.
  const [billingPicked, setBillingPicked] = useState<BillingMode | null>(null);
  const [km, setKm] = useState("");

  // The picker reads the same query, so this is one request.
  const customer = useQuery({
    queryKey: ["customer", customerId],
    queryFn: () => api.customers.get(customerId),
    enabled: Boolean(customerId),
  });
  const billing = billingPicked ?? defaultBillingMode(Boolean(customer.data?.is_contractor), type);
  const kmOneWay = parseKmOneWay(km);
  const sendsKm = billing !== "time_and_materials" && kmOneWay !== undefined && kmOneWay !== null;

  const board = useQuery({
    queryKey: ["dispatch", day, 1],
    queryFn: () => api.jobs.dispatch(day, 1),
    enabled: book,
  });

  useEffect(() => {
    if (!tech && board.data?.technicians[0]) setTech(board.data.technicians[0].user_id);
  }, [board.data, tech]);

  const hoursNum = Number(hours);
  const create = useMutation({
    mutationFn: async () => {
      // Built as a variable: `api.jobs.create`'s declared body predates billing_mode.
      const body = {
        customer_id: customerId,
        site_id: siteId,
        type,
        title: title.trim(),
        description: description.trim() || undefined,
        purchase_order_number: po.trim() || undefined,
        quoted_hours: hours && hoursNum > 0 ? hoursNum : undefined,
        reported_at: type === "callout" ? new Date().toISOString() : undefined,
        billing_mode: billing,
        ...(sendsKm ? { travel_km_one_way: kmOneWay } : {}),
      };
      const job = await api.jobs.create(body);
      if (book && tech) {
        await api.jobs.assign({
          job_id: job.id,
          user_id: tech,
          day,
          ...(at === "first" ? {} : { start_minute: Number(at) }),
        });
      }
      return job;
    },
    onSuccess: (job) => {
      void queryClient.invalidateQueries({ queryKey: ["dispatch"] });
      void queryClient.invalidateQueries({ queryKey: ["jobs"] });
      navigate(`/jobs/${job.id}`);
    },
    onError: (err) => setProblem(err instanceof ApiError ? err.message : "Couldn't create the job."),
  });

  return (
    <div className="page">
      <PageHeader title="New job">Who, where, and what's wrong. Booking it onto someone can wait.</PageHeader>

      <Card title="Customer and site">
        <CustomerSitePicker
          customerId={customerId}
          siteId={siteId}
          onCustomer={(id) => {
            setCustomerId(id);
            setBillingPicked(null);
          }}
          onSite={setSiteId}
        />
      </Card>

      <Card title="The job">
        <div className="form-grid">
          <label className="field">
            <span>Type</span>
            <select value={type} onChange={(e) => setType(e.target.value as JobType)}>
              {TYPES.map((t) => (
                <option key={t} value={t}>
                  {titleCase(t.replace("_", " "))}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Customer's order number</span>
            <input value={po} onChange={(e) => setPo(e.target.value)} />
          </label>
          <label className="field">
            <span>Expected hours</span>
            <input inputMode="decimal" value={hours} onChange={(e) => setHours(e.target.value)} placeholder="1" />
          </label>
        </div>
        {/* A quoted job bills its quote, so there is nothing to choose; this form has no quoted amount. */}
        <div className="form-grid" style={{ marginTop: 8 }}>
          <label className="field">
            <span>How this bills</span>
            <select value={billing} onChange={(e) => setBillingPicked(e.target.value as BillingMode)}>
              {(Object.keys(BILLING_LABEL) as BillingMode[]).map((m) => (
                <option key={m} value={m} disabled={m === "contractor" && !customer.data?.is_contractor}>
                  {BILLING_LABEL[m]}
                </option>
              ))}
            </select>
          </label>
          {billing !== "time_and_materials" && (
            <label className="field">
              <span>Km one way</span>
              <input
                inputMode="decimal"
                value={km}
                onChange={(e) => setKm(e.target.value)}
                placeholder="worked out from the address"
                aria-invalid={kmOneWay === null || undefined}
              />
            </label>
          )}
        </div>
        <label className="field" style={{ marginTop: 8 }}>
          <span>What it's for — the line on the run sheet</span>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Front gate intercom not ringing" />
        </label>
        <label className="field" style={{ marginTop: 8 }}>
          <span>Details for the tech</span>
          <textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
        {type === "callout" && (
          <p className="tiny muted">Logged as reported now — a site on a response agreement starts its clock from this moment.</p>
        )}
      </Card>

      <Card title="Booking">
        <label className="tiny" style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input type="checkbox" checked={book} onChange={(e) => setBook(e.target.checked)} />
          Book it onto someone now (otherwise it waits in Unassigned on Dispatch)
        </label>
        {book && (
          <div className="form-grid" style={{ marginTop: 8 }}>
            <label className="field">
              <span>Technician</span>
              <select value={tech} onChange={(e) => setTech(e.target.value)}>
                {(board.data?.technicians ?? []).map((t) => (
                  <option key={t.user_id} value={t.user_id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Day</span>
              <input type="date" value={day} onChange={(e) => setDay(e.target.value)} />
            </label>
            <label className="field">
              <span>Time</span>
              <select value={at} onChange={(e) => setAt(e.target.value)}>
                <option value="first">First gap</option>
                {Array.from({ length: SLOTS }, (_, i) => minuteOfSlot(i)).map((m) => (
                  <option key={m} value={m}>
                    {hhmm(m)}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}
      </Card>

      {problem && (
        <p className="notice notice-crit" role="alert">
          {problem}
        </p>
      )}
      <div className="btn-row">
        <button
          className="btn btn-primary"
          type="button"
          disabled={!customerId || !siteId || !title.trim() || create.isPending || (book && !tech) || kmOneWay === null}
          onClick={() => {
            setProblem(null);
            create.mutate();
          }}
        >
          {create.isPending ? "Creating…" : "Create job"}
        </button>
        <button className="btn" type="button" onClick={() => navigate(-1)}>
          Cancel
        </button>
      </div>
    </div>
  );
}
